/**
 * Students tab state and actions for the school management screen (ADM-8):
 * roster filter/sort, per-student analytics, CSV export, invitation resend,
 * usage reset and the student detail modal.
 *
 * State lives here (called by the screen), not in StudentsTab, so the chosen
 * filter and sort survive switching tabs.
 */
import React, { useState, type Dispatch, type SetStateAction } from 'react';

import { confirmDialog, showDialog } from '@/utils/dialogs';
import { apiRequest } from '../../services/apiClient';
import { isSchoolAdmin, isSuperAdmin, type User } from '../../utils/roleUtils';
import type {
  PendingInvitation,
  School,
  SchoolManagementTab,
  Student,
  StudentFilter,
  StudentSort,
} from './schoolManagementTypes';
import { filterAndSortStudents } from './schoolRoster';
import type { ToastType } from '@/components/ui/Toast';

type TFn = (key: string, defaultOrVars?: string | Record<string, any>, vars?: Record<string, any>) => string;

export function useSchoolStudents({
  user,
  school,
  students,
  setStudents,
  pendingInvites,
  activeTab,
  loadSchoolData,
  t,
  showToast,
}: {
  user?: User;
  school: School | null;
  students: Student[];
  setStudents: Dispatch<SetStateAction<Student[]>>;
  pendingInvites: PendingInvitation[];
  activeTab: SchoolManagementTab;
  loadSchoolData: () => Promise<void>;
  t: TFn;
  showToast: (message: string, type?: ToastType) => void;
}) {
  const [resettingStudentId, setResettingStudentId] = useState<number | null>(null);
  const [resendingInvitationId, setResendingInvitationId] = useState<number | null>(null);

  // Student filtering and sorting
  const [studentFilter, setStudentFilter] = useState<StudentFilter>('all');
  const [studentSort, setStudentSort] = useState<StudentSort>('name');
  const [loadingStudentAnalytics, setLoadingStudentAnalytics] = useState(false);

  // Student detail modal
  const [showStudentDetail, setShowStudentDetail] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);

  // Load enhanced student analytics
  const loadStudentAnalytics = React.useCallback(async () => {
    if (!school || students.length === 0) return;

    setLoadingStudentAnalytics(true);

    try {
      // Fetch analytics for each student
      const enhancedStudents = await Promise.all(
        students.map(async (student) => {
          try {
            const analytics = await apiRequest({
              endpoint: `/school-analytics/${school.id}/students/${student.id}/analytics`,
              method: 'GET'
            });
            if (analytics) {
              return {
                ...student,
                last_activity: analytics.last_activity,
                transcripts_this_month: analytics.transcripts_this_month,
                transcripts_total: analytics.transcripts_total,
                monthly_transcripts_limit: analytics.monthly_transcripts_limit,
                chat_messages_total: analytics.chat_messages_total,
                avg_icf_score: analytics.avg_icf_score,
              };
            }
          } catch (error) {
            console.error(`Failed to load analytics for student ${student.id}:`, error);
          }
          return student;
        })
      );

      setStudents(enhancedStudents);
    } catch (error) {
      console.error('Error loading student analytics:', error);
    } finally {
      setLoadingStudentAnalytics(false);
    }
  }, [school, students, setStudents]);

  // Export students to CSV
  const handleExportCSV = async () => {
    if (!school) return;

    try {
      // apiRequest handles auth (cookie or bearer), 401 refresh and error detail.
      const blob = await apiRequest<Blob>({
        endpoint: `/school-analytics/${school.id}/students/export`,
        method: 'GET',
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${school.slug}_students_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast(t('schoolManagement.exportStudentsSuccess'), 'success');
    } catch (error) {
      console.error('Failed to export students:', error);
      showDialog(t('common.error'), t('schoolManagement.exportStudentsError'));
    }
  };

  // Resend invitation
  const handleResendInvitation = async (invitationId: number) => {
    if (!school) return;

    try {
      setResendingInvitationId(invitationId);
      await apiRequest({
        endpoint: `/schools/${school.id}/student-invitations/${invitationId}/resend`,
        method: 'POST'
      });

      showToast(t('invitations.actions.resentTitle'), 'success');
      await loadSchoolData();
    } catch (error) {
      console.error('Failed to resend invitation:', error);
      showDialog(t('common.error'), t('schoolManagement.resendInvitationError'));
    } finally {
      setResendingInvitationId(null);
    }
  };

  // Filter and sort students (including invited)
  const getFilteredAndSortedStudents = () =>
    filterAndSortStudents(students, pendingInvites, studentFilter, studentSort);

  // Load analytics when students tab is active
  React.useEffect(() => {
    if (activeTab === 'students' && students.length > 0 && !loadingStudentAnalytics) {
      // Check if we already have analytics data
      const hasAnalytics = students.some((s) => s.transcripts_total !== undefined);
      if (!hasAnalytics) {
        loadStudentAnalytics();
      }
    }
  }, [activeTab, students, loadStudentAnalytics, loadingStudentAnalytics]);

  const handleResetUsage = async (student: Student) => {
    if (!(user && isSuperAdmin(user))) {
      return;
    }

    const confirmationMessage = t('schoolManagement.resetUsageConfirm', {
      name: student.full_name || student.email,
    });

    const confirmed = await confirmDialog(
      t('schoolManagement.resetUsage'),
      confirmationMessage,
      t('common.confirm'),
      t('common.cancel'),
    );

    if (!confirmed) return;

    try {
      setResettingStudentId(student.id);
      await apiRequest({
        endpoint: `/user-profile/admin/users/${student.id}/reset-usage`,
        method: 'POST'
      });

      showToast(t('schoolManagement.resetUsageSuccess'), 'success');
      await loadSchoolData();
    } catch (error) {
      console.error('Failed to reset student usage', error);
      const message = t('schoolManagement.resetUsageError');
      showDialog(message);
    } finally {
      setResettingStudentId(null);
    }
  };

  const handleOpenStudentDetail = (studentId: number) => {
    // Only allow if user is school admin or super admin
    if (!user || (!isSchoolAdmin(user) && !isSuperAdmin(user as User))) {
      showDialog(t('schoolManagement.accessRequired'));
      return;
    }

    console.log('[SchoolManagement] Opening student detail for ID:', studentId, 'in school:', school?.id);
    setSelectedStudentId(studentId);
    setShowStudentDetail(true);
  };

  const handleCloseStudentDetail = () => {
    setShowStudentDetail(false);
    setSelectedStudentId(null);
  };

  return {
    studentFilter,
    setStudentFilter,
    studentSort,
    setStudentSort,
    loadingStudentAnalytics,
    resettingStudentId,
    resendingInvitationId,
    showStudentDetail,
    selectedStudentId,
    getFilteredAndSortedStudents,
    handleExportCSV,
    handleResendInvitation,
    handleResetUsage,
    handleOpenStudentDetail,
    handleCloseStudentDetail,
  };
}

export type SchoolStudentsState = ReturnType<typeof useSchoolStudents>;
